import { useCallback, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiTokenRequest } from "@/config/auth";
import { registerTokenProvider } from "@/services/api";
import { appConfig } from "@/config/app";
import { trackEvent, trackException, setAuthenticatedUser } from "@/services/telemetry";

/**
 * Dev-mode token -- matches what the API's DevTokenValidator expects.
 * Only used when VITE_DEV_AUTH_BYPASS=true.
 */
const DEV_TOKEN = "dev-bypass-token";

/**
 * Acquires an access token silently (or via redirect) and registers
 * the token provider with the API service layer. Also pushes auth
 * identity context into Application Insights.
 *
 * In MSAL mode, the AuthGate component ensures the user is already
 * authenticated before this hook's getToken() is called by API
 * requests. This hook handles silent renewal and the edge case
 * where a cached session expires mid-use.
 */
export function useAuthToken(): {
  getToken: () => Promise<string | null>;
  isAuthenticated: boolean;
} {
  const { instance, accounts } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const authTracked = useRef(false);

  const isDevBypass = appConfig.flags.devAuthBypass;

  const getToken = useCallback(async (): Promise<string | null> => {
    // -- Dev bypass -------------------------------------------------
    if (isDevBypass) {
      if (!authTracked.current) {
        trackEvent("auth_token_acquired", { method: "dev_bypass" });
        authTracked.current = true;
      }
      return DEV_TOKEN;
    }

    // -- Production MSAL flow ---------------------------------------
    const account = accounts[0];
    if (!account) {
      // AuthGate should have triggered login before we get here.
      // If somehow we reach this point, return null and let the
      // 401 response surface the issue.
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });

      // Bind the AppInsights session to this user on first successful token
      if (!authTracked.current) {
        const userId = account.homeAccountId;
        const tenantId = account.tenantId;
        setAuthenticatedUser(userId, tenantId);
        trackEvent("auth_token_acquired", { method: "silent" });
        authTracked.current = true;
      }

      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Session expired or consent required -- redirect to re-authenticate.
        trackEvent("auth_interaction_required");
        await instance.acquireTokenRedirect(apiTokenRequest);
      } else {
        trackException(error, { context: "auth_token_acquisition" });
      }
      console.error("Token acquisition failed:", error);
      return null;
    }
  }, [instance, accounts, isDevBypass]);

  const isAuthenticated = isDevBypass || msalAuthenticated;

  // Register synchronously so the token provider is available before any
  // child component fires an API call. useEffect would run too late (after
  // children's effects), causing the first request to go out without a token.

  registerTokenProvider(getToken);

  return { getToken, isAuthenticated };
}
